import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import {
  GalleryLightbox,
  GalleryRow,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Unified photo gallery — every item in the property `photo_gallery`
 * collection, in one grid (with a shared lightbox). No Apartment / Community
 * title filter; photos without an image URL fall back to `original_url`.
 */
export function Gallery() {
  const rawImages = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  const images = rawImages.filter((img) => !!(img.image || img.original_url));
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-8 md:pt-12 pb-8 md:pb-12">
        <EditableCollectionBinding bindingPath="gallery.images" label="Gallery Images">
          {images.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <p className="font-body text-lg">Gallery images will be displayed here.</p>
            </div>
          ) : (
            <GalleryRow images={images} onSelect={setSelectedIndex} />
          )}
        </EditableCollectionBinding>
      </div>

      <GalleryLightbox
        images={images}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
      description:
        'Pulled from the property photo gallery. Every photo with an image appears in this grid.',
    },
  },
};

(Gallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Gallery;
